/**
 * 子證（IPOADD1）配額欄「該怎麼填」——依每個配額鍵宣告的**子證合併方式**（批151 裁示 98；覆核 🟡3）。
 *
 * `AddonIssueModal`（簽發子證）與〈兌換碼活動〉的 trial 效果（兌換時簽的也是子證）共用。
 *
 * ## 為什麼不能一律「以主證現值為底往上加」
 *
 * 批93-B 的子證配額一律取 max，所以表單的「＋N」助手是「主證現值＋N，寫回**絕對值**」。
 * 合併方式改成每鍵宣告之後，對 `sum`（加總）的鍵這樣做會**算兩次**：主證 1000、按 +100
 * → 子證填 1100 → 合併後 1000＋1100＝2100。所以：
 *
 * | 合併方式 | 輸入框填什麼 | 「＋N」助手 |
 * |---|---|---|
 * | `max`（預設） | 目標值（絕對值） | 以主證現值為底往上加（批93-B 起的行為，不變） |
 * | `latest` | 目標值——這張是最新一張，生效期間它的值就是合併後的值 | 同 `max` |
 * | `sum` | **增加量** | 只累加輸入框自己的值，**不讀主證** |
 * | `min` | 目標值——只有低於現行才有作用 | 不提供（往上加不會有任何效果） |
 * | `avg` | 目標值——與主證及其他生效子證平均 | 不提供（要達到某個合併值得看其他子證，助手算不準） |
 *
 * 判讀合併方式一律走 `lib/quotas.ts::quotaAddonMerge`（缺鍵或值域外＝`max`）。
 * ★ 送出的永遠是「輸入框的值」本身——這支只決定畫面說法與助手怎麼算，不改 payload 的形狀。
 *
 * ## 加購數量級距（11-01 裁示）
 *
 * 配額鍵宣告了 `addon_step`（例：100）時，子證上這個鍵只能填它的正整數倍（100、200、300…）。
 * `addonQuotaInputAttrs` 給輸入框的 `min`／`step`，`addonStepError` 在送出前先擋非倍數，「＋N」助手
 * 的結果無條件進位到下一個倍數。後端 `products.check_addon_quota_steps` 仍是唯一真相（422 `addon_quota_step`）。
 */
import type { QuotaSpec } from '@/api/ls'
import { type AddonMergeMode, quotaAddonMerge, quotaAddonStep } from '@/lib/quotas'

/** 「＋N」助手的算法：`from_current`＝以主證現值為底；`increment`＝只累加輸入框；`null`＝不提供。 */
export type AddonQuotaBump = 'from_current' | 'increment' | null

export interface AddonQuotaInputMeta {
  mode: AddonMergeMode
  /** 輸入框的 placeholder（「目標值」或「增加量」）。 */
  inputLabel: string
  /** 非預設合併方式的一句說明（`max` 不必講＝`null`）。 */
  note: string | null
  bump: AddonQuotaBump
}

/** 這個配額鍵在子證表單上的填法。 */
export function addonQuotaInputMeta(q: QuotaSpec | null | undefined): AddonQuotaInputMeta {
  const mode = quotaAddonMerge(q)
  switch (mode) {
    case 'sum':
      return {
        mode,
        inputLabel: '增加量',
        note: '加總：這一格填「增加量」，合併後＝主證＋各生效子證',
        bump: 'increment',
      }
    case 'latest':
      return {
        mode,
        inputLabel: '目標值',
        note: '取最新一張：這張生效期間，填的值就是合併後的值',
        bump: 'from_current',
      }
    case 'min':
      return {
        mode,
        inputLabel: '目標值',
        note: '取最小值：填的值低於現行才有作用（往上加沒有效果）',
        bump: null,
      }
    case 'avg':
      return {
        mode,
        inputLabel: '目標值',
        note: '取平均（四捨五入）：與主證及其他生效子證的值平均',
        bump: null,
      }
    default:
      return { mode: 'max', inputLabel: '目標值', note: null, bump: 'from_current' }
  }
}

/**
 * 「＋N」助手按下去之後輸入框的新值；這個合併方式不提供助手時回 `null`（呼叫端不動輸入框）。
 *
 * - `from_current`：輸入框有值就從它往上加，否則以主證現值為底（不限／未設定＝0）。
 * - `increment`：只累加輸入框自己的值（空＝0），**不讀主證**——`sum` 的子證值本身就是增加量。
 * 結果至少 1（子證配額只收正整數）。給了 `step`（加購級距）時結果**無條件進位到下一個倍數**、
 * 至少一階——主證現值不一定是倍數（例：主證 250、按 +100 → 400）。
 */
export function bumpAddonQuota(
  bump: AddonQuotaBump,
  typed: string | null | undefined,
  current: number | null | undefined,
  delta: number,
  step: number | null = null,
): string | null {
  if (bump === null) return null
  const base = Number((bump === 'increment' ? typed : typed || current) || 0)
  const safe = Number.isFinite(base) ? base : 0
  const next = Math.max(1, Math.floor(safe) + delta)
  if (step == null || step < 2) return String(next)
  return String(Math.max(step, Math.ceil(next / step) * step))
}

/** 子證輸入框的 `min`／`step`：宣告了加購級距＝以一階為單位，否則 1。 */
export function addonQuotaInputAttrs(q: QuotaSpec | null | undefined): { min: number; step: number } {
  const step = quotaAddonStep(q)
  return step == null ? { min: 1, step: 1 } : { min: step, step }
}

/**
 * 子證上這個鍵的值合不合加購級距；合（或沒宣告級距）回 `null`，不合回一句給操作者看的話。
 *
 * 判準與後端 `products.check_addon_quota_steps` 相同：必須是級距的**正整數倍**（0、負數、小數、空值都不合）。
 * `allowUnset: true` 時空值與 `0` 放行（＝後端 `template=True`）——加購模板的 null＝不設定、0＝留給送件填；
 * 表單上的空白／0 則是「這一項不送」或交給後端既有的 `invalid_quota_value`，不是級距這一關的事。
 * 輸入框的字串（`<input type="number">` 的 v-model 可能是 number）照收，空字串視同 `null`。
 */
export function addonStepError(
  q: QuotaSpec | null | undefined,
  value: number | string | null | undefined,
  opts: { allowUnset?: boolean } = {},
): string | null {
  const step = quotaAddonStep(q)
  if (step == null) return null
  const raw = typeof value === 'string' ? value.trim() : value
  const n = raw === '' || raw == null ? null : Number(raw)
  if (opts.allowUnset && (n == null || n === 0)) return null
  if (n != null && Number.isInteger(n) && n > 0 && n % step === 0) return null
  return `須為 ${step} 的倍數（${step}、${step * 2}、${step * 3}…）`
}
